import React, {useState, useEffect} from 'react';
import { createTheme, ThemeProvider, Typography, Grid, makeStyles, createStyles, Theme as AugmentedTheme } from '@material-ui/core';
import {ErrorBoundary} from 'react-error-boundary'
import Error from './components/Error';
import Loader from './components/Loader';
import Main from './Main';

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
      formTitle: {
          fontSize: number,
          fontWeight: number,
          color: string,
          lineHeight: string,
      };
      formDescription: {
          fontSize: number,
          fontWeight: number,
          letterSpacing: string,
          lineHeight: string,
          color: string,
          marginTop: string,
      },
      formControl: {
          margin: string,
          backgroundColor: string,
          border: string,
          borderRadius: number,
          padding: string,
          width: string,
          boxSizing: string,
          overflow: string,
      };
      formControlBorder: {
          borderTop: string,
      };
      inputLabel: {
          fontSize: number,
          letterSpacing: string,
          lineHeight: string,
          color: string,
          fontWeight: number,
          marginBottom: number,
          position: string,
          transform: string,
      },
      requiredLabel: {
          fontSize: number,
          letterSpacing: string,
          lineHeight: string,
          color: string,
          fontWeight: number,
          marginBottom: number,
          position: string,
          transform: string,
          [key: string]: any,
      },
      requiredField: {
          [key: string]: any,
      },
  }
  interface ThemeOptions {
    formTitle?: {
        fontSize: number,
        fontWeight: number,
        color: string,
        lineHeight: string,
    };
    formDescription: {
        fontSize: number,
        fontWeight: number,
        letterSpacing: string,
        lineHeight: string,
        color: string,
        marginTop: string,
    },
    formControl?: {
        margin: string,
        backgroundColor: string,
        border: string,
        borderRadius: number,
        padding: string,
        width: string,
        boxSizing: string,
        overflow: string,
    };
    formControlBorder?: {
        borderTop: string,
    };
    inputLabel?: {
        fontSize: number,
        letterSpacing: string,
        lineHeight: string,
        color: string,
        fontWeight: number,
        marginBottom: number,
        position: string,
        transform: string,
    },
    requiredLabel?: {
        fontSize: number,
        letterSpacing: string,
        lineHeight: string,
        color: string,
        fontWeight: number,
        marginBottom: number,
        position: string,
        transform: string,
        [key: string]: any,
    },
    requiredField?: {
        [key: string]: any,
    },
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  formTitle: {
      fontSize: 32,
      fontWeight: 400,
      color: '#202124',
      lineHeight: '135%',
  },
  formDescription: {
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: '.2px',
      lineHeight: '20px',
      color: '#202124',
      marginTop: '12px',
  },
  formControl: {
      margin: '12px 0',
      backgroundColor: '#fff',
      border: '1px solid #dadce0',
      borderRadius: 8,
      padding: '22px 24px 24px',
      width: '100%',
      boxSizing: 'border-box' as 'border-box',
      overflow: 'hidden',
  },
  formControlBorder: {
      borderTop: '8px solid #8645f9',
  },
  inputLabel: {
      fontSize: 16,
      letterSpacing: '.1px',
      lineHeight: '24px',
      color: '#202124',
      fontWeight: 400,
      marginBottom: 5,
      position: 'static' as 'static',
      transform: 'none',
  },
  requiredLabel: {
      fontSize: 16,
      letterSpacing: '.1px',
      lineHeight: '24px',
      color: '#202124',
      fontWeight: 400,
      marginBottom: 5,
      position: 'static' as 'static',
      transform: 'none',
      '&::after': {
          content: '" *"',
          color: '#d32f2f',
          fontWeight: 'bold',
      },
  },
  requiredField: {
      '& .MuiInput-underline:after': {
          borderBottom: '2px solid #d32f2f',
      },
      '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
              borderColor: '#d32f2f',
          },
      },
  },
});

const useStyles = makeStyles((theme: AugmentedTheme) => createStyles({
    wrapper: {
      backgroundColor: 'rgba(159, 107, 250, .2);',
    },
    copy: {
      paddingBottom: 10,
    }
}))

function ErrorFallback({ error }: any) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  )
}

function App() {
  const classes = useStyles();

  const [uid, setUid] = useState<string>('');
  const [params, setParams] = useState<any>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const renderApp = (uid: string) => {
    return (
      uid ? <ErrorBoundary FallbackComponent={ErrorFallback}><Main uid={uid}/></ErrorBoundary> : <Error/>
    )
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setParams(urlParams);
  }, [])

  useEffect(() => {
    if(params) {
      const uidFromPArams = params.get('uid');
      setUid(uidFromPArams);
    }

  }, [params])


  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500)
  }, [uid])

  return(  
  <ThemeProvider theme={theme}>
    <div className={classes.wrapper}>
      {isLoading ? <Loader/> : renderApp(uid)}
      <Grid container justifyContent="center">
        <Typography className={classes.copy}>&copy; Powered by <a href="https://www.tuqqi.com/" target="_blank" rel="noreferrer">Tuqqi</a> 2024</Typography>
      </Grid>
    </div>
  </ThemeProvider>
  ) 
}

export default App;
